import { useFormik } from 'formik'
import { FC, useMemo } from 'react'
import {
  ButtonType,
  showAlert,
  showConfirmAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import {
  Button,
  FormInput,
  FormTextArea,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'

import { faturaAtendimentosSchema } from './validation/faturaAtendimentosSchema'
import { atualizaItemFatura, inativaItemFatura } from '../../core/_requests'
import {
  AtualizaFaturaAtendimentoDto,
  AtualizaFaturaAtendimentoDtoValores,
} from '../../core/_models'
import { useT } from '_metronic/i18n/Metronici18n'

interface IProps {
  action: 'atualizar' | 'inativar'
  idFaturaAtendimento: number
  valorEmpresa?: number
  valorProfissional?: number
}

export const FaturaAtendimentosModal: FC<IProps> = ({
  action,
  idFaturaAtendimento,
  valorEmpresa,
  valorProfissional,
}) => {
  const { closeModal } = useModal()
  const { t } = useT()

  const formik = useFormik({
    initialValues: {
      observacao: '',
      action,
      valorProfissional,
      valorEmpresa,
    },
    validationSchema: useMemo(() => faturaAtendimentosSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (action === 'inativar') {
          const isConfirmed = await showConfirmAlert({
            text: t('alert.item_proximas_faturas'),
            confirmButtonText: t('common.yes'),
          })
          if (!isConfirmed) return
          const data: AtualizaFaturaAtendimentoDto = {
            observacao: values.observacao.trim() || undefined,
          }
          await inativaItemFatura(idFaturaAtendimento, data)
        } else {
          const data: AtualizaFaturaAtendimentoDtoValores = {
            observacao: values.observacao.trim() || undefined,
            valorEmpresa: values.valorEmpresa,
            valorProfissional: values.valorProfissional,
          }
          await atualizaItemFatura(idFaturaAtendimento, data)
        }

        showAlert({
          title: t('common.success'),
          text: t(action === 'atualizar' ? 'alert.atualizar' : 'alert.inativado', {
            name: t('item.label'),
            gender: t('item.gender'),
          }),
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      {action === 'atualizar' && (
        <div className='row'>
          <div className='col-lg-6 mb-3'>
            <FormInput
              formik={formik}
              name='valorEmpresa'
              type='number'
              entity='valor_empresa'
              isRequired
            />
          </div>
          <div className='col-lg-6 mb-3'>
            <FormInput
              formik={formik}
              name='valorProfissional'
              type='number'
              entity='valor_profissional'
              isRequired
            />
          </div>
        </div>
      )}
      <FormTextArea
        name='observacao'
        formik={formik}
        entity='observacao'
        isDisabled={formik.isSubmitting}
      />

      <div className='d-flex justify-content-center mt-10'>
        <Button className='btn-secondary me-3' disabled={formik.isSubmitting} onClick={closeModal}>
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
        </Button>
      </div>
    </form>
  )
}
